/**
 * Report-card chat adapter: the pure-orchestration seam between the chat UI and
 * the chatbot proxy. Given the running message list, it builds the proxy request
 * (latest turn as `message`, prior turns as grounding `history`, plus the
 * player-assessment scope and an optional access token), streams the proxy's SSE
 * frames, and yields the cumulatively-assembled assistant text so the UI can
 * render it as it arrives. No React, no DOM — state and abort wiring live in the
 * consuming hook; SSE transport lives in the SSE client.
 *
 * Future: the `run` async-generator shape intentionally mirrors assistant-ui's
 * `ChatModelAdapter` so a Phase-2 swap to that library can reuse this adapter
 * without reshaping call sites. If that swap never happens, the shape is still a
 * reasonable standalone contract and needs no change.
 */
import {
  IReportCardChatAdapter,
  IReportCardChatContext,
  IReportCardChatRunOptions,
  IReportCardChatRunResult,
} from './chatbot.types';
import { messageText, toWireHistory } from './chatbot.helpers';
import { streamChat } from './report-card-chat-sse-client';

export interface ICreateReportCardChatAdapterOptions {
  /** Full proxy URL including the /chatbot/chat path. */
  proxyUrl: string;
  /** Player-assessment scope + optional access token forwarded on every request. */
  context: IReportCardChatContext;
  /** Bearer token for the proxy (authenticated users). Optional. */
  authToken?: string;
}

// Shape mirrors assistant-ui's `ChatModelAdapter` (Phase-2 seam): `run` is an
// async generator yielding cumulative assistant content, as `useLocalRuntime` expects.
export const createReportCardChatAdapter = (
  { proxyUrl, context, authToken }: ICreateReportCardChatAdapterOptions
): IReportCardChatAdapter => ({
  async *run({ messages, abortSignal }: IReportCardChatRunOptions):
    AsyncGenerator<IReportCardChatRunResult, IReportCardChatRunResult | void, undefined> {

    // Latest user turn is the last message; the rest is grounding history — the
    // FastAPI service splits the same way (`{ history, message }`).
    const latest = messages[messages.length - 1];
    const history = messages.slice(0, -1);

    const body = {
      playerAssessmentId: context.playerAssessmentId,
      message: latest ? messageText(latest) : '',
      history: toWireHistory(history),
      // Token rides in the body so the proxy reads it even without a Bearer header
      // (anonymous public-report-card visitors); the proxy also accepts ?token=.
      ...(context.token ? { token: context.token } : {}),
    };

    let cumulative = '';
    let errored: string | null = null;

    try {
      for await (const frame of streamChat({ url: proxyUrl, body, abortSignal, authToken })) {
        if (frame.type === 'text') {
          cumulative += frame.text;
          yield { content: [{ type: 'text' as const, text: cumulative }] };
        } else if (frame.type === 'error') {
          errored = frame.error;
          break;
        } else if (frame.type === 'done') {
          break;
        }
      }
    } catch (err) {
      // AbortError is the user pressing stop — rethrow as a clean cancellation.
      // Anything else collapses to a generic message (see CLAUDE.md error rule).
      if ((err as Error)?.name === 'AbortError') {
        throw err;
      }
      console.error('createReportCardChatAdapter stream failed:', err);
      errored = "Sorry — I couldn't reach the chat service. Please try again.";
    }

    if (errored !== null) {
      // Throw so the hook routes through the reducer's `send/error` action — UI
      // shows a friendly banner while keeping any partial assistant text.
      throw new Error(errored);
    }

    return { content: [{ type: 'text' as const, text: cumulative }] };
  },
});
